import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import RosterTable from '../../components/roster/RosterTable';
import SearchBar from '../.././components/roster/SearchBar';
import MissingPhotosAlert from '../../components/roster/MissingPhotosAlert';
import CSVUpload from '../../components/roster/CSVUpload';
import { Artist } from '../../components/roster/types';
import { artistsApi } from '../../repositories/artists';
import Swal from 'sweetalert2';
import Pagination from '../../components/common/Pagination';
import { useDebounce } from 'use-debounce';

const Roster: React.FC = () => {
  const [artists, setArtists] = useState<Artist[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 400);
  const [showMissingPhotosAlert, setShowMissingPhotosAlert] = useState(false);
  const [showCSVUpload, setShowCSVUpload] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [missingPhotoArtists, setMissingPhotoArtists] = useState<Artist[]>([]);
  const [lastMissingPhotosCount, setLastMissingPhotosCount] = useState(0);

  useEffect(() => {
    loadArtists();
  }, [page, limit, debouncedSearchTerm]);

  const loadArtists = async () => {
    try {
      setLoading(true);
      const response = await artistsApi.getAll({ page, limit, search: debouncedSearchTerm });
      const artistsData = response.data.data?.artists || response.data.data || response.data;
      setArtists(artistsData);

      // If your backend returns pagination info:
      if (response.data.data?.pagination) {
        setTotalPages(response.data.data.pagination.totalPages);
        setTotalResults(response.data.data.pagination.totalResults || 0);
      }

      // Fetch missing photo artists after loading main list
      try {
        const missingPhotoRes = await artistsApi.getArtistsMissingPhotos();
        const missing = missingPhotoRes.data.data || [];
        setMissingPhotoArtists(missing);

        // Smart alert logic
        const dismissed = localStorage.getItem('missingPhotosAlertDismissed');
        const dismissedCount = localStorage.getItem('dismissedMissingPhotosCount');
        const currentCount = missing.length;

        if (currentCount > 0) {
          if (dismissed !== 'true') {
            // Never dismissed - always show
            setShowMissingPhotosAlert(true);
          } else {
            // Was dismissed, check if new artists were added
            const previousCount = dismissedCount ? parseInt(dismissedCount, 10) : 0;
            if (currentCount > previousCount) {
              setShowMissingPhotosAlert(true);
            }
          }
        } else {
          setShowMissingPhotosAlert(false);
        }
      } catch (error) {
        console.error('Error fetching missing photos:', error);
        setShowMissingPhotosAlert(false);
      }
    } catch (error: any) {
      console.error('Error loading artists:', error);
      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || error.response?.data?.error || 'Failed to load artists. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCSVUploadSuccess = () => {
    setShowCSVUpload(false);
    loadArtists();
  };

  const downloadCSVTemplate = () => {

    const headers = [
      'Artist Name*',
      'Spotify URI*',
      "Apple Artist ID*",
      'ASIN*',
      'Photo',
      'Bio',
      'Artist Hometown',
      'Artist Current Town',
      'Country',
      'Label*',
      'Distributor*',
      'Instagram*',
      'TikTok',
      'X',
      'Facebook',
      'Snapchat',
      'SoundCloud URL*',
      'TIDAL URL*',
      'Spotify URL',
      'Apple URL',
      'Amazon URL',
      'Deezer URL'
    ];

    const exampleData = [
      'Example Artist',
      '25BObiRSDCMwVrBGIVaLIf',
      '123456789',
      'B07XVQZXYZ',
      'https://example.com/photo.jpg',
      '"Artist bio here"',
      '"New York"',
      '"Los Angeles"',
      '"USA"',
      '"Example Records"',
      '"Example Distribution"',
      'https://instagram.com/example',
      'https://tiktok.com/@example',
      'https://x.com/example',
      'https://facebook.com/example',
      'https://snapchat.com/add/example',
      'https://soundcloud.com/example',
      'https://tidal.com/example',
      'https://open.spotify.com/artist/example',
      'https://music.apple.com/artist/example',
      'https://amazon.com/artist/example',
      'https://deezer.com/artist/example'
    ];

    // Create CSV content
    const csvLines = [
      headers.join(','),
      exampleData.join(',')
    ];
    const csvContent = csvLines.join('\n');

    // Add BOM for proper Excel encoding
    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csvContent;

    // Trigger download
    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'artist_roster_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    // Show success message
    Swal.fire({
      title: 'Template Downloaded',
      text: 'CSV template has been downloaded to your computer.',
      icon: 'success',
      confirmButtonText: 'OK'
    });
  };

  const handleMissingPhotosDismiss = (permanent: boolean = false) => {
    setShowMissingPhotosAlert(false);

    if (permanent) {
      // Store the current count when permanently dismissed
      localStorage.setItem('missingPhotosAlertDismissed', 'true');
      localStorage.setItem('dismissedMissingPhotosCount', missingPhotoArtists.length.toString());

      Swal.fire({
        title: 'Alert Dismissed',
        text: `Alert dismissed. You'll be notified again if more than ${missingPhotoArtists.length} artists are missing photos.`,
        icon: 'info',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleViewMissingPhotos = () => {
    // You can implement navigation to a filtered view or settings
    // For now, just close the alert
    setShowMissingPhotosAlert(false);

    // Optionally show a message
    Swal.fire({
      title: 'Missing Photos',
      text: `You have ${missingPhotoArtists.length} artists without photos. Consider adding photos to improve your roster presentation.`,
      icon: 'info',
      confirmButtonText: 'OK'
    });
  };

  const handleDeleteArtist = async (id: string) => {
    const result = await Swal.fire({
      title: 'Delete Artist?',
      text: 'Are you sure you want to delete this artist? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
    });

    if (result.isConfirmed) {
      try {
        await artistsApi.delete(id);
        Swal.fire('Deleted!', 'Artist has been deleted.', 'success');
        loadArtists(); // Refresh the list
      } catch (error: any) {
        Swal.fire('Error', error.response?.data?.message || 'Failed to delete artist.', 'error');
      }
    }
  };
  // Load the last count on component mount
  useEffect(() => {
    const savedCount = localStorage.getItem('lastMissingPhotosCount');
    if (savedCount) {
      setLastMissingPhotosCount(parseInt(savedCount, 10));
    }
  }, []);
  return (
    <div className="min-h-screen py-8 rounded shadow bg-gray-50">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-gray-900">Artist Roster</h1>
              <div className="flex space-x-4">
                <button
                  onClick={downloadCSVTemplate}
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                >
                  Download CSV Template
                </button>
                <button
                  onClick={() => setShowCSVUpload(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700"
                >
                  Import CSV
                </button>
                <Link
                  to="/roster/add-artist"
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                >
                  Add Artist
                </Link>
              </div>
            </div>
          </div>

          <div className="p-6">
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
            />

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="w-12 h-12 border-b-2 border-indigo-600 rounded-full animate-spin"></div>
              </div>
            ) : (
              <>
                <RosterTable artists={artists} onDelete={handleDeleteArtist} />

                <div className="flex flex-col items-center justify-center mt-4 sm:flex-row">
                  <Pagination
                    page={page}
                    totalPages={totalPages}
                    setPage={setPage}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showMissingPhotosAlert && (
        <MissingPhotosAlert
          artists={missingPhotoArtists}
          onDismiss={handleMissingPhotosDismiss}
          onViewArtists={handleViewMissingPhotos}
        />
      )}

      {showCSVUpload && (
        <CSVUpload
          onClose={() => setShowCSVUpload(false)}
          onSuccess={handleCSVUploadSuccess}
        />
      )}
    </div>
  );
};

export default Roster;